import React, { createContext, useContext, useState, useEffect } from 'react';
import { setSentryUser, clearSentryUser } from '../sentry';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start.
    //
    // Accept BOTH token storage keys — `__admin_token__` is set by the
    // /admin/login flow, `__login_user_token__` is set by the regular
    // /login flow. authedFetch and the api config already accept either;
    // AuthContext used to only see the user-token, which made AddonGate
    // (and anything else gated through useAuth) think an admin signed
    // in via /admin/login was unauthenticated. That caused a Navigate
    // to /login on every addon click — and Login.jsx's auto-redirect
    // effect then bounced the admin to /admin/dashboard because the
    // stored __user__ blob said role:"admin". Reading both keys here
    // closes that loop at its source.
    //
    // Same logic for the user blob: /admin/login writes __admin_user__,
    // regular /login writes __user__. Prefer the regular key so callers
    // that mutate __user__ (e.g. ProtectedRoute refreshing from the
    // server on every nav) stay authoritative when both are present.
    const token =
      localStorage.getItem('__login_user_token__') ||
      localStorage.getItem('__admin_token__') ||
      localStorage.getItem('token');
    const userData =
      localStorage.getItem('__user__') ||
      localStorage.getItem('__admin_user__') ||
      localStorage.getItem('user');

    try {
      const parsed = userData ? JSON.parse(userData) : null;
      if (token && parsed) {
        setUser(parsed);
        // Restore Sentry user context on page reload for already-logged-in users.
        setSentryUser(parsed);
      }
    } catch (err) {
      console.error('Failed to parse stored user data', err);
    }
    setLoading(false);
  }, []);

  // Push notification foreground handler. While the tab is open + focused
  // we want incoming FCM messages to render as in-app toasts instead of
  // OS banners (the service worker handles background pushes). Only
  // attach once we know there's a logged-in user with a stored FCM token —
  // otherwise we're spinning up Firebase Messaging on the login page for
  // no reason.
  useEffect(() => {
    if (!user) return;
    let unsubscribe = () => {};
    let cancelled = false;
    (async () => {
      try {
        const { setupForegroundHandler } = await import("../utils/pushNotifications");
        const toastMod = await import("react-hot-toast");
        const toast = toastMod.default;
        const handler = await setupForegroundHandler((msg) => {
          // Only honor URLs that resolve to the same origin. A push payload
          // is supposed to come from our backend, but defense in depth: if
          // the FCM token were ever leaked or our server were ever
          // compromised, an attacker could send a notification whose click
          // navigates the user to a phishing site. Restricting the click
          // target to same-origin closes that vector.
          let safeUrl = null;
          if (msg.url && typeof msg.url === "string") {
            try {
              const parsed = new URL(msg.url, window.location.origin);
              if (parsed.origin === window.location.origin) {
                safeUrl = parsed.pathname + parsed.search + parsed.hash;
              }
            } catch { /* invalid URL — leave safeUrl as null */ }
          }
          // Compact toast layout: bold title, secondary body, click to route.
          toast(
            (t) => (
              <div
                onClick={() => {
                  toast.dismiss(t.id);
                  if (safeUrl) window.location.href = safeUrl;
                }}
                style={{ cursor: safeUrl ? "pointer" : "default" }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{msg.title}</div>
                {msg.body ? (
                  <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>{msg.body}</div>
                ) : null}
              </div>
            ),
            { duration: 6000, position: "top-right" }
          );
        });
        if (!cancelled) unsubscribe = handler;
      } catch (err) {
        // Push isn't supported on this device, or Firebase isn't configured.
        // That's fine — the rest of the app works normally.
      }
    })();
    return () => { cancelled = true; unsubscribe(); };
  }, [user]);

  const login = (userData, token, refreshToken = null) => {
    // Persist under both key variants to remain compatible with code that
    // reads from either `token`/`user` or `__login_user_token__`/`__user__`.
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('__login_user_token__', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('__user__', JSON.stringify(userData));
      if (refreshToken) {
        localStorage.setItem('__refresh_token__', refreshToken);
      }
    } catch (err) {
      console.error('Failed to save user to localStorage', err);
    }
    setUser(userData);
    // Tag subsequent Sentry events with the new user's identity.
    setSentryUser(userData);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('__login_user_token__') || localStorage.getItem('token');
      const refreshToken = localStorage.getItem('__refresh_token__');
      const apiUrl = import.meta.env.VITE_API_URL || "https://stacked-backend-gv43.onrender.com/api/v1";

      // Call backend logout endpoint to revoke refresh tokens
      if (token) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        }).catch(() => {}); // Don't block logout if request fails
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all token keys
      localStorage.removeItem('token');
      localStorage.removeItem('__login_user_token__');
      localStorage.removeItem('__refresh_token__');
      localStorage.removeItem('__admin_token__');
      localStorage.removeItem('user');
      localStorage.removeItem('__user__');
      setUser(null);
      // Clear Sentry user scope so subsequent anonymous errors don't get
      // attributed to the now-logged-out user.
      clearSentryUser();
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
